import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { BUTTON_DISABLED_TEXT, FLAT_EARTH, MONOLITH } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { isUrlLocalized } from '../../utils/localization';
import { getLanguageUrl } from '../../utils/urls';
import { GlobeIcon } from '../GlobeIcon';
import { hrefLangOverrides } from 'developer-docs-shared-components/lib/locale/utils';
import { isMdxDoc } from 'developer-docs-mdx-components/utils/urls';
import UIButton from 'UIComponents/button/UIButton';
import UIScreenReaderOnly from 'UIComponents/text/UIScreenReaderOnly';
import { mediaQueries, MOBILE_MENU_BREAKPOINT } from 'developer-docs-shared-components/utils/mediaQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SelectedLang = styled.span.withConfig({
  displayName: "LanguageSwitcher__SelectedLang",
  componentId: "sc-1f6ddne-0"
})(["text-decoration:none;vertical-align:super;margin-left:5px;font-weight:500;font-size:14px;color:", ";"], ({
  $dropdownDisabled
}) => $dropdownDisabled ? BUTTON_DISABLED_TEXT : MONOLITH);
const LanguageSwitcherButton = styled(UIButton).attrs({
  use: 'unstyled'
}).withConfig({
  displayName: "LanguageSwitcher__LanguageSwitcherButton",
  componentId: "sc-1f6ddne-1"
})(["color:", ";&:hover{text-decoration:none;", "{text-decoration:underline;}}", "{margin-left:2vw;}", "{margin-left:54px;}"], MONOLITH, SelectedLang, mediaQueries.aboveWidth(MOBILE_MENU_BREAKPOINT), mediaQueries.aboveWidth(1512));

/**
 * These strings do not need to be translated, as they are the names of the languages in that language.
 */
const languages = {
  'ja-jp': '日本語',
  'de-de': 'Deutsch',
  'en-us': 'English',
  'es-es': 'Español',
  'pt-br': 'Português',
  'fr-fr': 'Français'
};

/**
 * A dropdown listing available languages for the current route.
 *
 * TODO: Non-translated routes (i.e. a route where there is no translation
 * available) should show as disabled / greyed-out, like the hubspot.com
 * site. Each language will have its own subdirectory at
 * `hubspot./_LANG_/_PATH_`.
 *
 * @see https://lucid.app/lucidchart/ec224d66-f178-40c2-835f-8a00c8872d04/edit?view_items=FDi1zImpvsIK&invitationId=inv_87e1fc42-52df-418e-bbb6-f6229d5eceae
 */
function LanguageSwitcher() {
  const locale = useLocale();
  const location = useLocation();
  const availableLanguages = useMemo(() => Object.keys(languages).filter(languageKey => isUrlLocalized(location.pathname, languageKey) || !isMdxDoc(location.pathname)), [location.pathname]);
  const availableLanguagesOtherThanCurrent = useMemo(() => availableLanguages.filter(languageKey => languageKey !== locale), [availableLanguages, locale]);
  const dropdownDisabled = availableLanguagesOtherThanCurrent.length === 0;
  const links = useMemo(() => {
    if (availableLanguagesOtherThanCurrent.length === 0) {
      return [];
    }
    return Object.keys(languages).map(languageKey => {
      const isCurrentLanguage = languageKey === locale;
      const isLocalizationAvailable = availableLanguages.includes(languageKey);
      const link = /*#__PURE__*/_jsx(UILink, {
        "data-test-id": "language-option",
        disabled: isCurrentLanguage || !isLocalizationAvailable,
        className: "text-center",
        href: getLanguageUrl(languageKey, location.pathname),
        hrefLang: hrefLangOverrides[languageKey] || languageKey,
        children: languages[languageKey]
      }, languageKey);
      if (isCurrentLanguage) {
        return /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.header.languageSwitcher.currentLanguage"
          }),
          children: link
        }, languageKey);
      }
      if (!isLocalizationAvailable) {
        return /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.header.languageSwitcher.translationUnavailable"
          }),
          children: link
        }, languageKey);
      }
      return link;
    });
  }, [availableLanguagesOtherThanCurrent.length, availableLanguages, locale, location.pathname]);
  return /*#__PURE__*/_jsx(UITooltip, {
    disabled: !dropdownDisabled,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "developer-docs-ui.header.languageSwitcher.noTranslationsAvailable"
    }),
    children: /*#__PURE__*/_jsx(UIDropdown, {
      "data-test-id": "language-switcher",
      disabled: dropdownDisabled,
      buttonUse: "transparent",
      Button: LanguageSwitcherButton,
      popoverProps: {
        arrowSize: 'medium',
        placement: 'top'
      },
      caretRenderer: () => /*#__PURE__*/_jsx(UIIcon, {
        color: dropdownDisabled ? BUTTON_DISABLED_TEXT : FLAT_EARTH,
        name: "down",
        size: 10
      }),
      menuWidth: 150,
      buttonText: /*#__PURE__*/_jsxs("div", {
        className: "align-center",
        children: [/*#__PURE__*/_jsx(UIScreenReaderOnly, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.header.languageSwitcher.selectALanguage"
          })
        }), /*#__PURE__*/_jsx(GlobeIcon, {
          color: dropdownDisabled ? BUTTON_DISABLED_TEXT : FLAT_EARTH,
          width: 15
        }), /*#__PURE__*/_jsx(SelectedLang, {
          $dropdownDisabled: dropdownDisabled,
          children: languages[locale]
        })]
      }),
      children: /*#__PURE__*/_jsx(UIList, {
        children: links
      })
    })
  });
}
export default LanguageSwitcher;